/**
 * Token resolution for the Try it panel.
 *
 * Three ways in, tried in this order:
 *   1. A token already in localStorage, put there by the dashboard or by an earlier
 *      visit. Shared storage only works because the docs sit on the same origin family.
 *   2. The session cookie. The platform sets `srid` on the parent domain, so any
 *      *.livepush.io page can trade it for a bearer token at GET /auth/access_token.
 *      This is the path that makes "already signed in" work with no extra steps.
 *   3. A token the reader pastes in by hand, for anyone signed out or on another domain.
 *
 * Tokens are held in memory and in localStorage. They are never sent anywhere except the
 * API host the request targets, and never written to the console or the page URL.
 */

import type { TryItSpec } from './spec.ts';

export type TokenSource = 'storage' | 'cookie' | 'manual';

export interface TokenState {
  token: string | null;
  source: TokenSource | null;
  /** True once the cookie exchange has been tried, so the UI stops saying "checking". */
  resolved: boolean;
}

/** Decodes a JWT payload without verifying it. Display only, never a trust decision. */
export function describeToken(token: string): { name?: string; scopes: string[] } | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { name?: string; scope?: unknown };
    const scope = payload.scope;
    const scopes = Array.isArray(scope)
      ? scope.map(String)
      : typeof scope === 'string'
        ? scope.split(/[,\s]+/).filter(Boolean)
        : [];
    return { name: payload.name, scopes };
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private window or blocked storage — the token still works for this page view */
  }
}

/**
 * Account switching in the dashboard writes the active account's token to the alias key
 * and keeps the original under the main key, so the alias wins when both are present.
 */
export function storedToken(spec: TryItSpec): string | null {
  return read(spec.auth.aliasKey) ?? read(spec.auth.tokenKey);
}

export function rememberToken(spec: TryItSpec, token: string): void {
  write(spec.auth.tokenKey, token);
}

export function forgetToken(spec: TryItSpec): void {
  for (const key of [spec.auth.tokenKey, spec.auth.aliasKey]) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing to clear */
    }
  }
}

let cookieExchange: Promise<string | null> | null = null;

/**
 * Trades the session cookie for a bearer token. Shared between panels on the page so
 * several of them cannot stampede the endpoint.
 *
 * A failure here is normal and not worth surfacing as an error: the reader is simply not
 * signed in, or the docs origin is not in the auth host's CORS allowlist yet.
 */
export function exchangeCookie(spec: TryItSpec): Promise<string | null> {
  if (cookieExchange) return cookieExchange;

  cookieExchange = (async () => {
    try {
      const response = await fetch(`${spec.auth.authApiBase}/auth/access_token`, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const body = (await response.json()) as { accessToken?: string };
      if (!body.accessToken) return null;
      rememberToken(spec, body.accessToken);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      cookieExchange = null;
    }
  })();

  return cookieExchange;
}

export async function resolveToken(spec: TryItSpec): Promise<TokenState> {
  const existing = storedToken(spec);
  if (existing) return { token: existing, source: 'storage', resolved: true };

  const fresh = await exchangeCookie(spec);
  if (fresh) return { token: fresh, source: 'cookie', resolved: true };

  return { token: null, source: null, resolved: true };
}

export function signInUrl(spec: TryItSpec): string {
  return `${spec.auth.loginUrl}?redirect=${encodeURIComponent(window.location.href)}`;
}
